import { CheckCircleIcon } from "@heroicons/react/24/solid";
import { cva } from "cva";
import Image from "next/image";
import { useAnalytics } from "~/components/Analytics";
import { BuildHTML } from "~/components/BuildHtml/BuildHtml";
import { CTALink } from "~/landing-pages/components/CTA/CTALink/CTALink";
import { primaryButtonStyle } from "~/landing-pages/components/CTA/cta-shared-styles";
import { sanitizeRelativeUrl } from "~/lib/shared/sanitizeRelativeUrl";
import { tagToClassNameMap } from "~/landing-pages/utils/tagToClassNameMap";
import { Body, Headline } from "~/ui-library";

export interface CTATwoColumnWithImageProps {
  blockIndex: number;
  appearance: "brand" | "dark" | "light";
  imageUrl: string;
  headline: string;
  description: string;
  bullets: {
    bulletPoint: string;
  }[];
  primaryCtaLink: string;
  primaryCtaText: string;
}

const blockStyle = cva(["relative py-12 sm:py-16 lg:py-24"], {
  defaultVariants: {
    appearance: "light",
  },
  variants: {
    appearance: {
      brand: "bg-brand-indigo",
      dark: "bg-gray-900",
      light: "bg-brand-indigo-50/10",
    },
  },
});

const innerBlockStyle = cva(
  [
    "mx-auto flex max-w-2xl flex-col gap-16  px-6 py-16 ring-1 sm:rounded-3xl sm:p-8 lg:mx-0 lg:max-w-none lg:flex-row lg:items-center lg:py-20 xl:gap-x-20 xl:px-20",
  ],
  {
    defaultVariants: {
      appearance: "light",
    },
    variants: {
      appearance: {
        brand: "bg-white/5 ring-white/10",
        dark: "bg-white/5 ring-white/10",
        light: "bg-white/90 shadow-3 ring-white/90",
      },
    },
  }
);

const headlineStyle = cva(["font-bold"], {
  defaultVariants: {
    appearance: "light",
  },
  variants: {
    appearance: {
      brand: "text-white",
      dark: "text-white",
      light: "text-gray-900",
    },
  },
});

const descriptionStyle = cva(["mt-6"], {
  defaultVariants: {
    appearance: "light",
  },
  variants: {
    appearance: {
      brand: "text-brand-indigo-200",
      dark: "text-gray-300",
      light: "text-gray-800",
    },
  },
});

const bulletListStyle = cva(
  ["mt-10 grid grid-cols-1 gap-x-8 gap-y-3 md:grid-cols-2"],
  {
    defaultVariants: {
      appearance: "light",
    },
    variants: {
      appearance: {
        brand: "text-white",
        dark: "text-white",
        light: "text-gray-900",
      },
    },
  }
);

export const CTATwoColumnWithImage = ({
  blockIndex,
  imageUrl,
  headline,
  appearance,
  description,
  bullets,
  primaryCtaLink,
  primaryCtaText,
}: CTATwoColumnWithImageProps): JSX.Element => {
  const { analyticsEnhancedTrack } = useAnalytics();
  const primaryButtonClick = () =>
    analyticsEnhancedTrack("Clicked Primary CTA", {
      blockIndex,
      appearance,
      linkText: primaryCtaText,
      linkUrl: primaryCtaLink,
    });
  return (
    <div className={blockStyle({ appearance })}>
      <div className="relative isolate">
        <div className="mx-auto max-w-7xl sm:px-6 lg:px-8">
          <div className={innerBlockStyle({ appearance })}>
            <Image
              className="aspect-square h-auto w-full flex-none rounded-2xl object-cover shadow-3 md:aspect-auto md:h-96 lg:aspect-square lg:h-auto lg:max-w-sm"
              width={320}
              height={320}
              src={imageUrl}
              alt=""
            />
            <div className="w-full flex-auto">
              <Headline
                level={1}
                as="h2"
                className={headlineStyle({ appearance })}
              >
                {headline}
              </Headline>
              <Body className={descriptionStyle({ appearance })}>
                <BuildHTML
                  html={description}
                  tagToClassNameMap={tagToClassNameMap}
                />
              </Body>
              <ul className={bulletListStyle({ appearance })}>
                {bullets.map((bullet) => (
                  <li key={bullet.bulletPoint} className="flex gap-x-3">
                    <CheckCircleIcon
                      className="h-7 w-5 flex-none"
                      aria-hidden="true"
                    />
                    {bullet.bulletPoint}
                  </li>
                ))}
              </ul>
              <div className="mt-10 flex">
                <CTALink
                  href={sanitizeRelativeUrl(primaryCtaLink)}
                  onClick={primaryButtonClick}
                  className={primaryButtonStyle({ appearance })}
                >
                  {primaryCtaText}
                </CTALink>
              </div>
            </div>
          </div>
        </div>
        <div
          className="absolute inset-x-0 -top-16 -z-10 flex transform-gpu justify-center overflow-hidden blur-3xl"
          aria-hidden="true"
        >
          <div
            className="aspect-[1318/752] w-[82.375rem] flex-none bg-gradient-to-r from-[#80caff] to-[#4f46e5] opacity-25"
            style={{
              clipPath:
                "polygon(73.6% 51.7%, 91.7% 11.8%, 100% 46.4%, 97.4% 82.2%, 92.5% 84.9%, 75.7% 64%, 55.3% 47.5%, 46.5% 49.4%, 45% 62.9%, 50.3% 87.2%, 21.3% 64.1%, 0.1% 100%, 5.4% 51.1%, 21.4% 63.9%, 58.9% 0.2%, 73.6% 51.7%)",
            }}
          />
        </div>
      </div>
    </div>
  );
};
